<template>
  <modal
    :loading="loading"
    :title="$t('llm_analyse.title')"
    style="max-width: 580px"
    @close_modal="close_modal"
  >
    <template v-slot:buttons>
      <primary-button
        :label="primaryRunLabel"
        :disabled="!can_primary_run"
        @pressed="run_primary"
      />
    </template>
    <template v-slot:content>
      <my-assets-run-target-banner
        :use-selection="useSelection"
        :selection-count="selectionCount"
        @clear-selection="clearSelectionAndUseFilter"
      />

      <p v-if="!runTargetIsSelection && !can_run_filter" class="text-caption text-grey-darken-1 mb-2">
        {{ $t("myassets.need_filter_for_batch") }}
      </p>

      <div class="d-flex flex-wrap align-center mb-3" style="gap: 4px 16px;">
        <v-switch
          v-model="do_find"
          class="mt-0"
          inset
          hide-details
          dense
          color="primary"
          :label="$t('llm_analyse.task_find')"
        />
        <v-switch
          v-model="do_verify"
          class="mt-0"
          inset
          hide-details
          dense
          color="primary"
          :label="$t('llm_analyse.task_verify')"
        />
        <v-switch
          v-model="do_llm_question"
          class="mt-0"
          inset
          hide-details
          dense
          color="primary"
          :label="$t('llm_analyse.task_llm_question')"
        />
        <v-switch
          v-model="do_llm_prompt_boxes"
          class="mt-0"
          inset
          hide-details
          dense
          color="primary"
          :label="$t('llm_analyse.task_llm_prompt_boxes')"
        />
      </div>

      <v-divider class="mb-3" />

      <div v-if="do_find">
        <p class="text-caption mb-2 font-weight-medium">{{ $t("llm_analyse.find_section_title") }}</p>
        <v-combobox
          v-model="subject_chips"
          class="subject-combobox"
          outlined
          dense
          chips
          small-chips
          clearable
          multiple
          :label="$t('llm_analyse.subjects_label')"
          :hint="$t('llm_analyse.subjects_hint')"
          persistent-hint
          :delimiters="[',']"
          :placeholder="$t('llm_analyse.subjects_placeholder')"
        />
      </div>

      <div v-if="do_verify" class="mt-3">
        <p class="text-caption mb-2 font-weight-medium">{{ $t("llm_analyse.verify_section_title") }}</p>
        <search-label-autocomplete
          :label_type="1"
          :field_label="$t('llm_analyse.verify_category_label')"
          :placeholder="$t('llm_analyse.verify_category_placeholder')"
          :value="verify_category_obj"
          @label-selected="on_verify_category_select"
          @cleared="on_verify_category_cleared"
        />
        <p class="text-caption mt-2 mb-0">{{ $t("llm_analyse.verify_hint") }}</p>
      </div>

      <div v-if="do_llm_question" class="mt-3">
        <p class="text-caption mb-2 font-weight-medium">{{ $t("llm_analyse.question_section_title") }}</p>
        <v-combobox
          v-model="llm_question_input"
          outlined
          dense
          clearable
          :items="recentQuestions"
          :label="$t('llm_analyse.question_label')"
          :hint="$t('llm_analyse.question_hint')"
          persistent-hint
          :placeholder="$t('llm_analyse.question_placeholder')"
        />
        <v-text-field
          v-model="llm_question_meta_key"
          class="mt-2"
          outlined
          dense
          :label="$t('llm_analyse.question_meta_key_label')"
          :hint="$t('llm_analyse.question_meta_key_hint')"
          persistent-hint
          :placeholder="$t('llm_analyse.question_meta_key_placeholder')"
        />
      </div>

      <div v-if="do_llm_prompt_boxes" class="mt-3">
        <p class="text-caption mb-2 font-weight-medium">{{ $t("llm_analyse.prompt_boxes_section_title") }}</p>
        <v-textarea
          v-model="llm_prompt_boxes_text"
          outlined
          dense
          rows="5"
          auto-grow
          :label="$t('llm_analyse.prompt_boxes_question_label')"
          :hint="$t('llm_analyse.prompt_boxes_question_hint')"
          persistent-hint
          :placeholder="$t('llm_analyse.prompt_boxes_question_placeholder')"
        />
        <search-label-autocomplete
          class="mt-2"
          :label_type="1"
          :field_label="$t('llm_analyse.prompt_boxes_label_field')"
          :placeholder="$t('llm_analyse.prompt_boxes_label_placeholder')"
          :value="prompt_boxes_label_obj"
          @label-selected="on_prompt_boxes_label_select"
          @cleared="on_prompt_boxes_label_cleared"
        />
        <p class="text-caption mt-2 mb-0">{{ $t("llm_analyse.prompt_boxes_hint") }}</p>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import PrimaryButton from "@/components/form/PrimaryButton";
import SearchLabelAutocomplete from "@/components/SearchLabelAutocomplete.vue";
import MyAssetsRunTargetBanner from "@/components/myassets/MyAssetsRunTargetBanner.vue";
import { flatFilters } from "@/components/asset_filter/filterQuery";

const RECENT_QUESTIONS_STORAGE_KEY = "llm_analyse_recent_questions_v1";
const MAX_RECENT_QUESTIONS = 5;

export default {
  name: "LlmAnalyseModal",
  components: {
    SearchLabelAutocomplete,
    PrimaryButton,
    Modal,
    MyAssetsRunTargetBanner,
  },
  props: {
    pageFilters: { type: [Array, Object, Boolean], default: false },
    selectedAssetIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      do_find: false,
      do_verify: false,
      do_llm_question: false,
      do_llm_prompt_boxes: false,
      subject_chips: [],
      verify_category_obj: null,
      verify_category_id: null,
      prompt_boxes_label_obj: null,
      prompt_boxes_label_id: null,
      useSelection: false,
      userClearedSelection: false,
      llm_question_input: "",
      llm_question_meta_key: "",
      llm_prompt_boxes_text: "",
      recentQuestions: [],
    };
  },
  computed: {
    selectionAssetIds() {
      return (this.selectedAssetIds || [])
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id));
    },
    selectionCount() {
      return this.selectionAssetIds.length;
    },
    runTargetIsSelection() {
      return this.useSelection && this.can_run_selection;
    },
    primaryRunLabel() {
      return this.runTargetIsSelection
        ? this.$t('llm_analyse.run_on_selection')
        : this.$t('llm_analyse.run_current_filter');
    },
    can_primary_run() {
      if (this.runTargetIsSelection) {
        return this.can_submit_selection;
      }
      return this.can_submit_filter;
    },
    can_run_filter() {
      return flatFilters(this.pageFilters || []).length > 0;
    },
    can_run_selection() {
      return this.selectionAssetIds.length > 0;
    },
    subjects_text() {
      const parts = (this.subject_chips || [])
        .map((s) => (typeof s === "string" ? s.trim() : String(s).trim()))
        .filter(Boolean);
      return parts.join(", ");
    },
    can_submit() {
      if (!this.do_find && !this.do_verify && !this.do_llm_question && !this.do_llm_prompt_boxes) {
        return false;
      }
      if (this.do_find && !this.subjects_text) {
        return false;
      }
      if (this.do_verify && this.verify_category_id == null) {
        return false;
      }
      if (this.do_llm_question) {
        const q = typeof this.llm_question_input === "string"
          ? this.llm_question_input.trim()
          : String(this.llm_question_input || "").trim();
        const k = (this.llm_question_meta_key || "").trim();
        if (!q || !k) {
          return false;
        }
      }
      if (this.do_llm_prompt_boxes) {
        const t = (this.llm_prompt_boxes_text || "").trim();
        if (!t || this.prompt_boxes_label_id == null) {
          return false;
        }
      }
      return true;
    },
    can_submit_filter() {
      return this.can_submit && this.can_run_filter;
    },
    can_submit_selection() {
      return this.can_submit && this.can_run_selection;
    },
  },
  mounted() {
    this.userClearedSelection = false;
    this.useSelection = this.can_run_selection;
    this.loadRecentQuestions();
  },
  watch: {
    selectedAssetIds: {
      handler() {
        if (this.userClearedSelection) {
          return;
        }
        if (this.can_run_selection) {
          this.useSelection = true;
        } else if (this.useSelection) {
          this.useSelection = false;
        }
      },
    },
    do_find(enabled) {
      if (enabled) {
        this.do_verify = false;
        this.do_llm_question = false;
        this.do_llm_prompt_boxes = false;
      } else {
        this.subject_chips = [];
      }
    },
    do_verify(enabled) {
      if (enabled) {
        this.do_find = false;
        this.do_llm_question = false;
        this.do_llm_prompt_boxes = false;
      } else {
        this.verify_category_obj = null;
        this.verify_category_id = null;
      }
    },
    do_llm_question(enabled) {
      if (enabled) {
        this.do_find = false;
        this.do_verify = false;
        this.do_llm_prompt_boxes = false;
      } else {
        this.llm_question_input = "";
        this.llm_question_meta_key = "";
      }
    },
    do_llm_prompt_boxes(enabled) {
      if (enabled) {
        this.do_find = false;
        this.do_verify = false;
        this.do_llm_question = false;
      } else {
        this.llm_prompt_boxes_text = "";
        this.prompt_boxes_label_obj = null;
        this.prompt_boxes_label_id = null;
      }
    },
  },
  methods: {
    loadRecentQuestions() {
      try {
        const raw = localStorage.getItem(RECENT_QUESTIONS_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        this.recentQuestions = Array.isArray(parsed)
          ? parsed.filter((x) => typeof x === "string" && x.trim()).slice(0, MAX_RECENT_QUESTIONS)
          : [];
      } catch (_) {
        this.recentQuestions = [];
      }
    },
    rememberQuestion(questionText) {
      const q = (questionText || "").trim();
      if (!q) {
        return;
      }
      const next = [q, ...this.recentQuestions.filter((x) => x !== q)].slice(0, MAX_RECENT_QUESTIONS);
      this.recentQuestions = next;
      try {
        localStorage.setItem(RECENT_QUESTIONS_STORAGE_KEY, JSON.stringify(next));
      } catch (_) {
        /* ignore quota */
      }
    },
    close_modal() {
      this.$emit("close_modal");
    },
    clearSelectionAndUseFilter() {
      this.userClearedSelection = true;
      this.useSelection = false;
      this.$emit("clear-selection");
    },
    run_primary() {
      if (this.runTargetIsSelection) {
        this.run_on_selection();
        return;
      }
      this.run_on_filter();
    },
    on_verify_category_select(label) {
      if (!label || label.id == null) {
        this.verify_category_id = null;
        this.verify_category_obj = null;
        return;
      }
      this.verify_category_id = label.id;
      this.verify_category_obj = label;
    },
    on_verify_category_cleared() {
      this.verify_category_id = null;
      this.verify_category_obj = null;
    },
    on_prompt_boxes_label_select(label) {
      if (!label || label.id == null) {
        this.prompt_boxes_label_id = null;
        this.prompt_boxes_label_obj = null;
        return;
      }
      this.prompt_boxes_label_id = label.id;
      this.prompt_boxes_label_obj = label;
    },
    on_prompt_boxes_label_cleared() {
      this.prompt_boxes_label_id = null;
      this.prompt_boxes_label_obj = null;
    },
    validate() {
      if (!this.do_find && !this.do_verify && !this.do_llm_question && !this.do_llm_prompt_boxes) {
        this.$store.dispatch("snackbars/add_failed_snack", this.$t("llm_analyse.need_step"));
        return false;
      }
      if (this.do_find && !this.subjects_text) {
        this.$store.dispatch("snackbars/add_failed_snack", this.$t("llm_analyse.need_subjects"));
        return false;
      }
      if (this.do_verify && !this.verify_category_id) {
        this.$store.dispatch("snackbars/add_failed_snack", this.$t("llm_analyse.need_verify_category"));
        return false;
      }
      if (this.do_llm_question) {
        const q = typeof this.llm_question_input === "string"
          ? this.llm_question_input.trim()
          : String(this.llm_question_input || "").trim();
        if (!q) {
          this.$store.dispatch("snackbars/add_failed_snack", this.$t("llm_analyse.need_question"));
          return false;
        }
        if (!(this.llm_question_meta_key || "").trim()) {
          this.$store.dispatch("snackbars/add_failed_snack", this.$t("llm_analyse.need_question_meta_key"));
          return false;
        }
      }
      if (this.do_llm_prompt_boxes) {
        const t = (this.llm_prompt_boxes_text || "").trim();
        if (!t) {
          this.$store.dispatch("snackbars/add_failed_snack", this.$t("llm_analyse.need_prompt_boxes_text"));
          return false;
        }
        if (this.prompt_boxes_label_id == null) {
          this.$store.dispatch("snackbars/add_failed_snack", this.$t("llm_analyse.need_prompt_boxes_label"));
          return false;
        }
      }
      return true;
    },
    payload() {
      const q = typeof this.llm_question_input === "string"
        ? this.llm_question_input.trim()
        : String(this.llm_question_input || "").trim();
      const data = {
        find_query: this.subjects_text,
        do_find: this.do_find,
        do_verify: this.do_verify,
        do_llm_question: this.do_llm_question,
        llm_question: q,
        llm_question_meta_key: (this.llm_question_meta_key || "").trim(),
        do_llm_prompt_boxes: this.do_llm_prompt_boxes,
        llm_prompt_boxes_text: (this.llm_prompt_boxes_text || "").trim(),
        llm_prompt_boxes_label_id: this.prompt_boxes_label_id,
      };
      if (this.do_verify && this.verify_category_id != null) {
        data.category_label_id = this.verify_category_id;
      }
      return data;
    },
    async run_on_filter() {
      if (!this.validate() || !this.can_run_filter) {
        return;
      }
      this.loading = true;
      try {
        await this.$api.asset.llmAnalyseStart({
          filter: this.pageFilters,
          ...this.payload(),
        });
        if (this.do_llm_question) {
          this.rememberQuestion(this.llm_question_input);
        }
        this.$store.dispatch("snackbars/add_success_snack", this.$t("llm_analyse.queued"));
        this.close_modal();
      } catch (e) {
        this.$store.dispatch("snackbars/add_failed_snack", this.$t("llm_analyse.failed"));
      } finally {
        this.loading = false;
      }
    },
    async run_on_selection() {
      if (!this.runTargetIsSelection || !this.validate()) {
        return;
      }
      this.loading = true;
      try {
        await this.$api.asset.llmAnalyseStart({
          asset_ids: this.selectionAssetIds,
          ...this.payload(),
        });
        if (this.do_llm_question) {
          this.rememberQuestion(this.llm_question_input);
        }
        this.$store.dispatch("snackbars/add_success_snack", this.$t("llm_analyse.queued"));
        this.close_modal();
      } catch (e) {
        this.$store.dispatch("snackbars/add_failed_snack", this.$t("llm_analyse.failed"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.subject-combobox ::v-deep .v-select__selections {
  min-height: 40px;
}
</style>
